import { gatewayApi as api } from './gatewayApi';

// Tool Fixture Interfaces
export interface ToolFixture {
  Id: string;
  TFNum: string;
  Description: string;
  SupplierId: string;
  PricePerUnit: number;
  DepartmentId: string;
  LeadTime: string;
  MinStockLevel: number;
  MinOrderQty: number;
  CalibrationRequired: boolean;
  ToolTypeId: string;
  SerialNum?: string;
  Active: boolean;
  CreatedDate: string;
  ModifiedDate: string;
  CreatedBy: string;
  ModifiedBy: string;
  
  // Related entities
  Supplier?: Supplier;
  Department?: Department;
  ToolType?: ToolType;
  CalibrationCalendar?: CalibrationCalendarDetail[];
}

export interface Supplier {
  Id: string;
  Name: string;
  ContactPerson?: string;
  Email?: string;
  Phone?: string;
  Address?: string;
  Active: boolean;
}

export interface Department {
  Id: string;
  Name: string;
  Description?: string;
  Active: boolean;
}

export interface ToolType {
  Id: string;
  Name: string;
  Description?: string;
  Active: boolean;
}

export interface CalibrationCalendarDetail {
  Id: string;
  ToolFixtureId: string;
  EventName: string;
  Description?: string;
  EventDate: string;
  Completed: boolean;
  Active: boolean;
  CreatedDate: string;
  ModifiedDate: string;
}

// Request/Response Types
export interface CreateToolFixtureRequest {
  TFNum: string;
  Description: string;
  SupplierId: string;
  PricePerUnit: number;
  DepartmentId: string;
  LeadTime: string;
  MinStockLevel: number;
  MinOrderQty: number;
  CalibrationRequired: boolean;
  ToolTypeId: string;
  SerialNum?: string;
  Active: boolean;
}

export interface UpdateToolFixtureRequest extends Partial<CreateToolFixtureRequest> {
  Id: string;
}

export interface SearchToolFixturesRequest {
  pageNum?: number;
  pageSize?: number;
  sort?: string;
  filter?: {
    TFNum?: string;
    Description?: string;
    CalibrationRequired?: boolean;
    Active?: boolean;
    SupplierId?: string;
    DepartmentId?: string;
    ToolTypeId?: string;
  };
}

export interface SearchToolFixturesResponse {
  items: ToolFixture[];
  totalCount: number;
  pageNum: number;
  pageSize: number;
}

export interface CreateCalibrationRequest {
  ToolFixtureId: string;
  EventName: string;
  Description?: string;
  EventDate: string;
  Active: boolean;
}

export interface UpdateCalibrationRequest extends Partial<CreateCalibrationRequest> {
  Id: string;
}

// Statistics Interface
export interface ToolFixtureStatistics {
  totalTools: number;
  activeTools: number;
  toolsRequiringCalibration: number;
  upcomingCalibrations: number;
  overdueMaintenance: number;
  averageLeadTime: number;
}

// API Service
export const toolFixtureApi = {
  // Tool Fixture CRUD Operations
  getAll: (params?: SearchToolFixturesRequest): Promise<SearchToolFixturesResponse> => {
    console.log('[toolFixtureApi] getAll called with params:', params);
    console.log('[toolFixtureApi] Making POST request to /toolfixture/search');
    return api.post<SearchToolFixturesResponse>('/toolfixture/search', params || {}).then((response: any) => {
      console.log('[toolFixtureApi] getAll raw response:', response);
      
      // Extract data from response structure
      const extractedData = response?.data || response?.items || response || [];
      const items = Array.isArray(extractedData) ? extractedData : [];
      
      // Build proper SearchToolFixturesResponse structure
      const searchResponse: SearchToolFixturesResponse = {
        items: items,
        totalCount: response?.totalCount || response?.total_count || items.length,
        pageNum: response?.pageNum || response?.page_num || 1,
        pageSize: response?.pageSize || response?.page_size || 25
      };
      
      console.log('[toolFixtureApi] getAll response received:', {
        success: true,
        responseType: typeof response,
        isArray: Array.isArray(response),
        hasItems: !!searchResponse?.items,
        itemsLength: searchResponse?.items?.length || 0,
        totalCount: searchResponse?.totalCount,
        pageNum: searchResponse?.pageNum,
        pageSize: searchResponse?.pageSize,
        extractedDataLength: items.length
      });
      return searchResponse;
    }).catch(error => {
      console.error('[toolFixtureApi] getAll error:', {
        error: error,
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        timestamp: new Date().toISOString()
      });
      throw error;
    });
  },

  getById: (id: string): Promise<ToolFixture> =>
    api.get(`/toolfixture/${id}`),

  create: (data: CreateToolFixtureRequest): Promise<ToolFixture> =>
    api.post('/toolfixture/', data),

  update: (data: UpdateToolFixtureRequest): Promise<ToolFixture> =>
    api.put('/toolfixture/', data),

  delete: (id: string): Promise<void> =>
    api.delete(`/toolfixture/${id}`),

  // Dropdown Data
  getSuppliers: (): Promise<Supplier[]> => {
    console.log('[toolFixtureApi] getSuppliers called');
    console.log('[toolFixtureApi] Making GET request to /supplier/');
    return api.get<Supplier[]>('/supplier/').then((response: any) => {
      console.log('[toolFixtureApi] getSuppliers raw response:', response);
      
      // Extract data from response structure
      const extractedData = response?.data || response?.items || response || [];
      const suppliers = Array.isArray(extractedData) ? extractedData : [];
      
      console.log('[toolFixtureApi] getSuppliers response received:', {
        success: true,
        responseType: typeof response,
        isArray: Array.isArray(response),
        length: Array.isArray(response) ? response.length : 'not array',
        extractedDataLength: suppliers.length,
        extractedData: suppliers
      });
      return suppliers;
    }).catch(error => {
      console.error('[toolFixtureApi] getSuppliers error:', {
        error: error,
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        timestamp: new Date().toISOString()
      });
      throw error;
    });
  },

  getDepartments: (): Promise<Department[]> => {
    console.log('[toolFixtureApi] getDepartments called');
    console.log('[toolFixtureApi] Making GET request to /department/');
    return api.get<Department[]>('/department/').then((response: any) => {
      console.log('[toolFixtureApi] getDepartments raw response:', response);
      
      // Extract data from response structure
      const extractedData = response?.data || response?.items || response || [];
      const departments = Array.isArray(extractedData) ? extractedData : [];
      
      console.log('[toolFixtureApi] getDepartments response received:', {
        success: true,
        responseType: typeof response,
        isArray: Array.isArray(response),
        length: Array.isArray(response) ? response.length : 'not array',
        extractedDataLength: departments.length,
        extractedData: departments
      });
      return departments;
    }).catch(error => {
      console.error('[toolFixtureApi] getDepartments error:', {
        error: error,
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        timestamp: new Date().toISOString()
      });
      throw error;
    });
  },

  getToolTypes: (): Promise<ToolType[]> => {
    console.log('[toolFixtureApi] getToolTypes called');
    console.log('[toolFixtureApi] Making GET request to /tooltype/');
    return api.get<ToolType[]>('/tooltype/').then((response: any) => {
      console.log('[toolFixtureApi] getToolTypes raw response:', response);
      
      // Extract data from response structure
      const extractedData = response?.data || response?.items || response || [];
      const toolTypes = Array.isArray(extractedData) ? extractedData : [];
      
      console.log('[toolFixtureApi] getToolTypes response received:', {
        success: true,
        responseType: typeof response,
        isArray: Array.isArray(response),
        length: Array.isArray(response) ? response.length : 'not array',
        extractedDataLength: toolTypes.length,
        extractedData: toolTypes
      });
      return toolTypes;
    }).catch(error => {
      console.error('[toolFixtureApi] getToolTypes error:', {
        error: error,
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        timestamp: new Date().toISOString()
      });
      throw error;
    });
  },

  // Calibration Calendar Operations
  getCalibrationEvents: (toolFixtureId: string): Promise<CalibrationCalendarDetail[]> =>
    api.get(`/calibrationcalendardetail/`, { filter: { ToolFixtureId: toolFixtureId } }),

  createCalibrationEvent: (data: CreateCalibrationRequest): Promise<CalibrationCalendarDetail> =>
    api.post('/calibrationcalendardetail/', data),

  updateCalibrationEvent: (data: UpdateCalibrationRequest): Promise<CalibrationCalendarDetail> =>
    api.put('/calibrationcalendardetail/', data),

  deleteCalibrationEvent: (id: string): Promise<void> =>
    api.delete(`/calibrationcalendardetail/${id}`),

  // Statistics - REMOVED due to Lambda conversion errors
  // getStatistics: async (): Promise<ToolFixtureStatistics> => {
  //   try {
  //     // Try to call the statistics endpoint first
  //     return await api.get('/toolfixture/statistics');
  //   } catch (error) {
  //     console.warn('ToolFixture statistics endpoint not available, calculating from main data:', error);
      
  //     // Fallback: calculate statistics from main data
  //     try {
  //       const toolFixtures: any = await api.post('/toolfixture/search', { page_size: 1000 });
  //       const items = toolFixtures?.data || toolFixtures?.items || toolFixtures || [];
          
  //       return toolFixtureApi.calculateStatistics(Array.isArray(items) ? items : []);
  //     } catch (fallbackError) {
  //       console.error('Failed to calculate statistics from main data:', fallbackError);
  //       // Return default statistics
  //       return {
  //         totalTools: 0,
  //         activeTools: 0,
  //         toolsRequiringCalibration: 0,
  //         upcomingCalibrations: 0,
  //         overdueMaintenance: 0,
  //         averageLeadTime: 0,
  //       };
  //     }
  //   }
  // },

  // Utility Functions
  validateToolFixture: (toolFixture: Partial<ToolFixture>): string[] => {
    const errors: string[] = [];

    if (!toolFixture.TFNum?.trim()) {
      errors.push('Tool/Fixture Number is required');
    }

    if (!toolFixture.Description?.trim()) {
      errors.push('Description is required');
    }

    if (!toolFixture.SupplierId) {
      errors.push('Supplier is required');
    }

    if (!toolFixture.DepartmentId) {
      errors.push('Department is required');
    }

    if (!toolFixture.ToolTypeId) {
      errors.push('Tool Type is required');
    }

    if (toolFixture.MinStockLevel !== undefined && toolFixture.MinStockLevel < 0) {
      errors.push('Minimum Stock Level must be non-negative');
    }

    if (toolFixture.MinOrderQty !== undefined && toolFixture.MinOrderQty < 1) {
      errors.push('Minimum Order Quantity must be at least 1');
    }

    if (toolFixture.PricePerUnit !== undefined && toolFixture.PricePerUnit < 0) {
      errors.push('Price Per Unit must be non-negative');
    }

    return errors;
  },

  validateCalibrationEvent: (event: Partial<CalibrationCalendarDetail>): string[] => {
    const errors: string[] = [];

    if (!event.EventName?.trim()) {
      errors.push('Event Name is required');
    }

    if (!event.EventDate) {
      errors.push('Event Date is required');
    } else {
      const eventDate = new Date(event.EventDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (eventDate < today) {
        errors.push('Event Date cannot be in the past');
      }
    }

    return errors;
  },

  formatToolFixtureNumber: (tfNum: string): string => {
    // Format tool/fixture number according to business rules
    return tfNum.toUpperCase().replace(/[^A-Z0-9-]/g, '');
  },

  // calculateStatistics function removed - no longer needed
  // calculateStatistics: (toolFixtures: ToolFixture[]): ToolFixtureStatistics => {
  //   const totalTools = toolFixtures.length;
  //   const activeTools = toolFixtures.filter(tf => tf.Active).length;
  //   const toolsRequiringCalibration = toolFixtures.filter(tf => tf.CalibrationRequired).length;
    
  //   // Calculate upcoming calibrations (within next 30 days)
  //   const thirtyDaysFromNow = new Date();
  //   thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    
  //   const upcomingCalibrations = toolFixtures.reduce((count, tf) => {
  //     if (tf.CalibrationCalendar) {
  //       const upcoming = tf.CalibrationCalendar.filter(cal => {
  //         const eventDate = new Date(cal.EventDate);
  //         return !cal.Completed && eventDate <= thirtyDaysFromNow;
  //       }).length;
  //       return count + upcoming;
  //     }
  //     return count;
  //   }, 0);
    
  //   // Calculate overdue maintenance (past due date)
  //   const today = new Date();
  //   today.setHours(0, 0, 0, 0);
    
  //   const overdueMaintenance = toolFixtures.reduce((count, tf) => {
  //     if (tf.CalibrationCalendar) {
  //       const overdue = tf.CalibrationCalendar.filter(cal => {
  //         const eventDate = new Date(cal.EventDate);
  //         return !cal.Completed && eventDate < today;
  //       }).length;
  //       return count + overdue;
  //     }
  //     return count;
  //   }, 0);
    
  //   // Calculate average lead time (placeholder - would need actual data)
  //   const averageLeadTime = 0;
    
  //   return {
  //     totalTools,
  //     activeTools,
  //     toolsRequiringCalibration,
  //     upcomingCalibrations,
  //     overdueMaintenance,
  //     averageLeadTime,
  //   };
  // },

  exportToCSV: (toolFixtures: ToolFixture[]): string => {
    const headers = [
      'TF Number',
      'Description',
      'Supplier',
      'Department',
      'Tool Type',
      'Price Per Unit',
      'Lead Time (Days)',
      'Min Stock Level',
      'Min Order Qty',
      'Calibration Required',
      'Serial Number',
      'Active',
      'Created Date',
      'Modified Date'
    ];

    const rows = toolFixtures.map(tf => [
      tf.TFNum,
      tf.Description,
      tf.Supplier?.Name || '',
      tf.Department?.Name || '',
      tf.ToolType?.Name || '',
      tf.PricePerUnit?.toString() || '0',
      tf.LeadTime || '',
      tf.MinStockLevel?.toString() || '0',
      tf.MinOrderQty?.toString() || '0',
      tf.CalibrationRequired ? 'Yes' : 'No',
      tf.SerialNum || '',
      tf.Active ? 'Yes' : 'No',
      new Date(tf.CreatedDate).toLocaleDateString(),
      new Date(tf.ModifiedDate).toLocaleDateString()
    ]);

    return [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
  },

  downloadCSV: (csvContent: string, filename: string): void => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}; 